import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SLOT_ROLES,
  bandChemistryPreview,
  type Artist,
  type Band,
  type ChemistryPreview,
  type ChemistrySwap,
  type SlotRole,
} from "../../infra/api.js";
import type { AxisDelta } from "../../domain/index.js";
import { useArtistStore } from "../../stores/artistStore.js";
import { useBandStore } from "../../stores/bandStore.js";
import { Button } from "../../ui/Button.js";
import { PersonaCard } from "./PersonaCard.js";
import { SonicTraitsCard } from "./SonicTraitsCard.js";

const ROLE_LABELS: Record<SlotRole, string> = {
  vocal: "Vocal",
  guitar: "Guitarra",
  bass: "Baixo",
  drums: "Bateria",
  keys: "Teclado",
};

// How long a selector change settles before the preview endpoint is hit — a
// rapid change does not spam the (free) endpoint.
const PREVIEW_DEBOUNCE_MS = 300;

const EMPTY_SLOT = "__empty__";

interface Props {
  band: Band;
}

/**
 * The chemistry A/B — "e se fosse…?". Per slot, a selector lets the user
 * hypothetically swap the artist; the band aggregate is recomputed by the free,
 * pure `chemistry-preview` endpoint and shown side by side (current vs.
 * hypothetical) with the diff highlighted. A separate CTA fires the REAL slot
 * update — the experiment is free, the change is explicit (spec §6.3).
 */
export function BandChemistry({ band }: Props): JSX.Element {
  const artists = useArtistStore((s) => s.artists);
  // The hypothetical artist chosen per slot, distinct from the band's real
  // slots. `EMPTY_SLOT` = "(slot vazio)"; an absent key = "no swap on this slot".
  const [draft, setDraft] = useState<Partial<Record<SlotRole, string>>>({});
  const [preview, setPreview] = useState<ChemistryPreview | null>(null);
  const [loading, setLoading] = useState(false);
  // `previewError` is owned by the preview effect (and reset by it as the draft
  // changes). `applyError` is owned by `applyFormation` — kept SEPARATE so that
  // clearing the draft after a failed apply (which re-fires the preview effect)
  // cannot wipe the apply error before the user sees it (F9).
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applying, setApplying] = useState<SlotRole | null>(null);
  const error = applyError ?? previewError;

  useEffect(() => {
    if (artists.length === 0) void useArtistStore.getState().fetch();
  }, [artists.length]);

  // The current artist occupying each real slot, keyed by role.
  const currentByRole = useMemo(() => {
    const map: Partial<Record<SlotRole, Artist>> = {};
    for (const slot of band.slots) map[slot.slot_role] = slot.artist;
    return map;
  }, [band.slots]);

  // Build the swap list — only roles whose draft differs from the real slot.
  const swaps = useMemo<ChemistrySwap[]>(() => {
    const out: ChemistrySwap[] = [];
    for (const role of SLOT_ROLES) {
      const chosen = draft[role];
      if (chosen === undefined) continue;
      const currentId = currentByRole[role]?.id ?? null;
      const nextId = chosen === EMPTY_SLOT ? null : chosen;
      if (nextId !== currentId) out.push({ slot_role: role, artist_id: nextId });
    }
    return out;
  }, [draft, currentByRole]);

  const swapsKey = useMemo(() => JSON.stringify(swaps), [swaps]);
  // A monotonic token so a late preview response cannot overwrite a newer one.
  const reqToken = useRef(0);

  const runPreview = useCallback(
    (currentSwaps: ChemistrySwap[]): void => {
      const token = ++reqToken.current;
      setLoading(true);
      setPreviewError(null);
      bandChemistryPreview(band.id, currentSwaps)
        .then(({ preview: result }) => {
          if (token !== reqToken.current) return;
          setPreview(result);
          setLoading(false);
        })
        .catch((e: unknown) => {
          if (token !== reqToken.current) return;
          setPreviewError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        });
    },
    [band.id],
  );

  // Debounce the preview against the swap set so rapid selector changes settle.
  // `swapsKey` is the JSON of `swaps` — parsing it back keeps the effect's deps
  // stable (a fresh `swaps` array each render would re-fire it needlessly).
  useEffect(() => {
    const currentSwaps = JSON.parse(swapsKey) as ChemistrySwap[];
    if (currentSwaps.length === 0) {
      reqToken.current++; // cancel any in-flight response
      setPreview(null);
      setLoading(false);
      setPreviewError(null);
      return;
    }
    const timer = setTimeout(() => runPreview(currentSwaps), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [swapsKey, runPreview]);

  function setSlotDraft(role: SlotRole, value: string): void {
    // A fresh draft change clears a stale apply error from a previous attempt.
    setApplyError(null);
    setDraft((d) => {
      const next = { ...d };
      // Selecting the real current artist again clears the draft for that slot.
      const currentId = currentByRole[role]?.id ?? EMPTY_SLOT;
      if (value === currentId) delete next[role];
      else next[role] = value;
      return next;
    });
  }

  function resetDraft(): void {
    setDraft({});
  }

  async function applyFormation(): Promise<void> {
    // Apply each drafted swap as a REAL slot update — the explicit change.
    // The swaps are applied one at a time, so a mid-batch failure leaves the
    // band PARTIALLY changed. On any failure stop, surface the error, and
    // resync: reload the band from the server and clear the draft so the A/B
    // diff is recomputed against the real state — never a misleading mix of
    // applied-and-unapplied swaps.
    setApplyError(null);
    try {
      for (const swap of swaps) {
        setApplying(swap.slot_role);
        if (swap.artist_id === null) {
          await useBandStore.getState().removeSlot(band.id, swap.slot_role);
        } else {
          await useBandStore.getState().setSlot(band.id, swap.slot_role, swap.artist_id);
        }
      }
      resetDraft();
    } catch (e) {
      setApplyError(e instanceof Error ? e.message : String(e));
      // Resync to the server's real state — some swaps may already be applied.
      try {
        await useBandStore.getState().refresh(band.id);
      } catch {
        // A failed resync must not mask the original apply error.
      }
      // Drop the draft so the diff reflects the real (post-partial) formation.
      // `applyError` is a separate state, so this draft reset (which re-fires
      // the preview effect) does not clear the error the user must see.
      resetDraft();
    } finally {
      setApplying(null);
    }
  }

  const dirty = swaps.length > 0;

  return (
    <div className="flex flex-col gap-5">
      <p className="font-mono text-[10px] leading-relaxed text-ink-dim">
        Troque um membro e veja o som mudar — sem gerar nada, sem custo. A formação só muda de
        verdade quando você <span className="text-paper">aplica</span>.
      </p>

      {/* Per-slot "e se fosse…?" selectors */}
      <div className="flex flex-col divide-y divide-border border border-border bg-tape">
        {SLOT_ROLES.map((role) => {
          const current = currentByRole[role];
          const chosen = draft[role];
          const value = chosen ?? current?.id ?? EMPTY_SLOT;
          const changed = draft[role] !== undefined;
          return (
            <div key={role} className="grid grid-cols-12 items-center gap-3 px-4 py-2.5">
              <div className="col-span-3">
                <p className="font-mono text-[11px] uppercase tracking-widest2 text-paper">
                  {ROLE_LABELS[role]}
                </p>
                <p className="font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
                  atual: {current?.name ?? "vago"}
                </p>
              </div>
              <div className="col-span-8">
                <select
                  value={value}
                  aria-label={`E se ${ROLE_LABELS[role]} fosse`}
                  onChange={(e) => setSlotDraft(role, e.target.value)}
                  className={`w-full border bg-asphalt px-3 py-1.5 font-mono text-xs text-paper focus:outline-none ${
                    changed ? "border-oscillo" : "border-border focus:border-paper"
                  }`}
                >
                  <option value={EMPTY_SLOT}>(slot vazio)</option>
                  {artists.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.id === current?.id ? " · atual" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-1 text-right">
                {changed && (
                  <span
                    className="font-mono text-[9px] uppercase tracking-widest2 text-oscillo"
                    aria-label={`${ROLE_LABELS[role]} alterado`}
                  >
                    ↻
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <p
          role="alert"
          className="border border-vinyl/40 bg-vinyl/5 p-3 font-mono text-xs uppercase tracking-widest2 text-vinyl"
        >
          {error}
        </p>
      )}

      {!dirty && (
        <p className="border border-dashed border-border px-4 py-6 text-center font-mono text-[11px] uppercase tracking-widest2 text-ink-dim">
          escolha uma troca acima pra ver o antes / depois
        </p>
      )}

      {dirty && loading && !preview && (
        <p className="border border-border bg-tape px-4 py-6 text-center font-mono text-[11px] uppercase tracking-widest2 text-oscillo">
          calculando química…
        </p>
      )}

      {dirty && preview && (
        <div
          className={`flex flex-col gap-4 transition-opacity ${loading ? "opacity-50" : "opacity-100"}`}
          aria-busy={loading}
        >
          {/* A/B side-by-side */}
          <div className="grid grid-cols-2 gap-3">
            <ChemistrySidePanel
              tag="ATUAL"
              accent="border-border"
              traits={preview.current.traits}
              persona={preview.current.persona}
              promptSnippet={preview.current.promptSnippet}
            />
            <ChemistrySidePanel
              tag="HIPOTÉTICO"
              accent="border-oscillo"
              traits={preview.hypothetical.traits}
              persona={preview.hypothetical.persona}
              promptSnippet={preview.hypothetical.promptSnippet}
            />
          </div>

          {/* Highlighted diff */}
          <DiffPanel traits={preview.delta.traits} persona={preview.delta.persona} />

          {/* Apply CTA — the real change */}
          <div className="flex items-center justify-between border border-butter/40 bg-butter/5 px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted">
              {swaps.length} {swaps.length === 1 ? "troca" : "trocas"} · grátis até aplicar
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={resetDraft} disabled={applying !== null}>
                descartar
              </Button>
              <Button
                variant="primary"
                onClick={() => void applyFormation()}
                disabled={applying !== null}
              >
                {applying ? `aplicando ${ROLE_LABELS[applying]}…` : "aplicar esta formação"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One column of the A/B — the aggregated traits + persona + the prompt text. */
function ChemistrySidePanel({
  tag,
  accent,
  traits,
  persona,
  promptSnippet,
}: {
  tag: string;
  accent: string;
  traits: ChemistryPreview["current"]["traits"];
  persona: ChemistryPreview["current"]["persona"];
  promptSnippet: string;
}): JSX.Element {
  return (
    <div className={`flex flex-col gap-3 border-l-2 ${accent} bg-tape p-4`}>
      <p
        className={`catalog-num ${accent === "border-oscillo" ? "text-oscillo" : "text-ink-muted"}`}
      >
        {tag}
      </p>
      <div>
        <p className="mb-1.5 font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">som</p>
        <SonicTraitsCard traits={traits} emptyHint="sem traços sonoros" />
      </div>
      <div>
        <p className="mb-1.5 font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">
          persona
        </p>
        <PersonaCard persona={persona} emptyHint="sem persona" />
      </div>
      <div>
        <p className="mb-1 font-mono text-[9px] uppercase tracking-widest2 text-ink-dim">prompt</p>
        {promptSnippet.trim() ? (
          <p className="font-mono text-[10px] leading-relaxed text-ink-muted">{promptSnippet}</p>
        ) : (
          <p className="font-mono text-[10px] italic leading-relaxed text-ink-dim">prompt neutro</p>
        )}
      </div>
    </div>
  );
}

/** The merged trait+persona diff — green added / red removed / amber changed. */
function DiffPanel({ traits, persona }: { traits: AxisDelta; persona: AxisDelta }): JSX.Element {
  const added = [...traits.added, ...persona.added];
  const removed = [...traits.removed, ...persona.removed];
  const changed = [...traits.changed, ...persona.changed];
  const empty = added.length === 0 && removed.length === 0 && changed.length === 0;

  return (
    <div className="border border-border bg-asphalt/60 px-4 py-3" aria-label="Diferenças">
      <p className="catalog-num mb-2 text-paper">O QUE MUDA</p>
      {empty ? (
        <p className="font-mono text-[11px] italic text-ink-dim">
          nada muda — esta formação soa igual à atual
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {added.map((d) => (
            <DiffChip key={`a-${d}`} kind="added" text={d} />
          ))}
          {removed.map((d) => (
            <DiffChip key={`r-${d}`} kind="removed" text={d} />
          ))}
          {changed.map((d) => (
            <DiffChip key={`c-${d}`} kind="changed" text={d} />
          ))}
        </div>
      )}
    </div>
  );
}

function DiffChip({
  kind,
  text,
}: {
  kind: "added" | "removed" | "changed";
  text: string;
}): JSX.Element {
  const styles: Record<typeof kind, string> = {
    added: "border-moss bg-moss/15 text-moss",
    removed: "border-vinyl bg-vinyl/15 text-vinyl",
    changed: "border-butter bg-butter/15 text-butter",
  };
  const verb: Record<typeof kind, string> = {
    added: "adicionado",
    removed: "removido",
    changed: "alterado",
  };
  return (
    <span
      aria-label={`${verb[kind]}: ${text}`}
      className={`inline-flex items-center border px-2 py-0.5 font-mono text-[10px] lowercase tracking-widest2 ${styles[kind]}`}
    >
      {text}
    </span>
  );
}
